import { makeAutoObservable } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import type { InfiniteData } from "@tanstack/react-query";
import type {
  IProjectTemplatesPage,
  TPickableProjectTemplate,
} from "@/services/project-template.service";
import { peekQueryClient } from "@/queries/client";
import {
  patchTemplatePages,
  pickableTemplatesQuery,
  projectTemplatesKey,
  templateDetailKey,
  templatesPagesPrefix,
} from "@/queries/project-templates";
import type { RootStore } from "./root.store";
import { queryCacheScope } from "./query-cache-scope";
import type {
  TCreateProjectFromTemplateOverrides,
  TCreatedProjectFromTemplate,
  TCreateProjectTemplatePayload,
  TProjectTemplateDetail,
  TProjectTemplateListItem,
  TTemplateCustomFieldEntry,
  TUpdateProjectTemplatePayload,
  TWorkitemTemplate,
} from "@/types/project-template";
import { appendProject, projectCacheFor } from "@/queries/projects";

export class ProjectTemplatesStore {
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /** One template the caller may use, for a form to prefill from: from the
   *  cached pickable list (`app/queries/project-templates.ts`) the picker
   *  shows, or straight from the API before the session resolves. */
  async fetchPickableTemplate(
    workspaceSlug: string,
    id: string,
  ): Promise<TPickableProjectTemplate> {
    const scope = queryCacheScope(workspaceSlug, "ProjectTemplatesStore");
    const client = scope ? peekQueryClient() : null;
    const rows =
      scope && client
        ? await client.fetchQuery(
            pickableTemplatesQuery(this.rootStore.projectTemplateService, scope),
          )
        : await this.rootStore.projectTemplateService.listPickableTemplates(workspaceSlug);
    const template = rows.find((row) => row.id === id);
    if (!template) throw new Error("Template is not available to use");
    return template;
  }

  /**
   * Project a detail response onto a list row.
   *
   * Team Access (#2926) echoes the server's teams so a scoping change shows on
   * the list without a refetch. `can_edit` is deliberately `null`, NOT copied
   * from the detail response: the detail value answers "can I edit THIS one",
   * and pasting it onto a list row would assert it for a row the API never
   * evaluated. Only the `?team=` list path carries a real per-row `can_edit`,
   * and that one arrives from the server.
   */
  private toListRow(detail: TProjectTemplateDetail): TProjectTemplateListItem {
    return {
      id: detail.id,
      template_name: detail.template_name,
      template_description: detail.template_description,
      cover_image: detail.cover_image,
      privacy: detail.privacy,
      member_count: detail.members?.length ?? 0,
      team_access: detail.team_access ?? [],
      can_edit: null,
      created_at: detail.created_at,
      updated_at: detail.updated_at,
    };
  }

  /**
   * After a confirmed write, mark every cached template read
   * (`app/queries/project-templates.ts`) stale, so an open template picker
   * re-reads: at once if it is on screen, on its next mount if not.
   */
  private staleCachedTemplates(slug: string): void {
    const scope = queryCacheScope(slug, "ProjectTemplatesStore");
    if (!scope) return;
    const client = peekQueryClient();
    const warn = (error: unknown) => {
      console.warn("[ProjectTemplatesStore] invalidating the cached templates failed", {
        slug,
        error,
      });
    };
    client?.invalidateQueries({ queryKey: projectTemplatesKey(scope) }).catch(warn);
    // The settings table's pages are only marked stale: the ones on screen
    // keep the rows `patchTablePages` gave them, and the next visit re-reads.
    client
      ?.invalidateQueries({ queryKey: templatesPagesPrefix(scope), refetchType: "none" })
      .catch(warn);
  }

  /**
   * Show a confirmed write in the settings table's cached pages
   * (`patchTemplatePages`), without a re-read that would re-sort or re-filter
   * the rows under the person who made it. Never rejects: the write committed.
   */
  private async patchTablePages(
    slug: string,
    change: (rows: TProjectTemplateListItem[], firstPage: boolean) => TProjectTemplateListItem[],
    countDelta: (data: InfiniteData<IProjectTemplatesPage>) => number = () => 0,
  ): Promise<void> {
    const scope = queryCacheScope(slug, "ProjectTemplatesStore");
    const client = scope ? peekQueryClient() : null;
    if (!scope || !client) return;
    await patchTemplatePages(client, scope, change, countDelta).catch((error: unknown) => {
      console.warn("[ProjectTemplatesStore] patching the cached table pages failed", {
        slug,
        error,
      });
    });
  }

  /**
   * Put a confirmed write's answer in the cached detail
   * (`app/queries/project-templates.ts`) that the template pages read, so an
   * open tab shows it without a re-read. `update` patches it (the work-item
   * custom-field write patches one row); `null` removes it (a delete).
   */
  private patchCachedTemplate(
    slug: string,
    id: string,
    update:
      | ((cached: TProjectTemplateDetail | undefined) => TProjectTemplateDetail | undefined)
      | null,
  ): void {
    const scope = queryCacheScope(slug, "ProjectTemplatesStore");
    const client = scope ? peekQueryClient() : null;
    if (!scope || !client) return;
    const key = templateDetailKey(scope, id);
    if (update === null) {
      client.removeQueries({ queryKey: key, exact: true });
      return;
    }
    client.setQueryData<TProjectTemplateDetail>(key, update);
  }

  /** The template as it stood before a write, for its analytics diff: the
   *  cached detail, if a page has loaded it. */
  private templateBefore(slug: string, id: string): TProjectTemplateDetail | undefined {
    const scope = queryCacheScope(slug, "ProjectTemplatesStore");
    const cached = scope
      ? peekQueryClient()?.getQueryData<TProjectTemplateDetail>(templateDetailKey(scope, id))
      : undefined;
    return cached;
  }

  async createTemplate(
    workspaceSlug: string,
    data: TCreateProjectTemplatePayload,
    source: AnalyticsSource = "page",
  ): Promise<TProjectTemplateDetail> {
    const detail = await this.rootStore.projectTemplateService.createTemplate(workspaceSlug, data);
    this.patchCachedTemplate(workspaceSlug, detail.id, () => detail);
    this.staleCachedTemplates(workspaceSlug);
    track("template_created", { scope: "project", template_id: detail.id, source });
    return detail;
  }

  /**
   * Duplicate a template and show the copy immediately (#2926).
   *
   * The copy lands at the top of the settings table regardless of the current sort or
   * search: it is the direct result of the user's action, and dropping it
   * because it doesn't match the active query would read as the action having
   * failed. The next page load re-sorts it into place.
   *
   * `teamId` scopes the copy to the team tab it was made from (GH-4093); absent
   * from workspace Settings, where the copy stays workspace-wide.
   */
  async duplicateTemplate(
    workspaceSlug: string,
    id: string,
    teamId?: string,
    source: AnalyticsSource = "page",
  ): Promise<TProjectTemplateDetail> {
    const detail = await this.rootStore.projectTemplateService.duplicateTemplate(
      workspaceSlug,
      id,
      teamId,
    );
    const row = this.toListRow(detail);
    await this.patchTablePages(
      workspaceSlug,
      (rows, firstPage) => (firstPage ? [row, ...rows.filter((r) => r.id !== row.id)] : rows),
      () => 1,
    );
    this.patchCachedTemplate(workspaceSlug, detail.id, () => detail);
    this.staleCachedTemplates(workspaceSlug);
    // A copy is a new template, so it keeps template_created (renaming would split
    // the event's history) and names the template it came from.
    track("template_created", {
      scope: "project",
      template_id: detail.id,
      duplicated_from_id: id,
      source,
    });
    return detail;
  }

  async updateTemplate(
    workspaceSlug: string,
    id: string,
    data: TUpdateProjectTemplatePayload,
    source: AnalyticsSource = "page",
  ): Promise<TProjectTemplateDetail> {
    // Read before the write replaces it: `fields` is what this save changed. The
    // detail tabs send their whole form, so the body's keys alone would name
    // every field on every save. A template that isn't loaded counts every key.
    const fields = changedFields(data, this.templateBefore(workspaceSlug, id));
    const detail = await this.rootStore.projectTemplateService.updateTemplate(
      workspaceSlug,
      id,
      data,
    );
    const patched = this.toListRow(detail);
    // Keep the settings table's row in sync (inline name / privacy edits)
    // without a refetch, and keep the row's server-computed `can_edit`: on the
    // `?team=` list it is a real verdict, and `toListRow`'s `null` would
    // silently unlock a view-only row after an unrelated edit elsewhere.
    await this.patchTablePages(workspaceSlug, (rows) =>
      rows.map((item) => (item.id === detail.id ? { ...patched, can_edit: item.can_edit } : item)),
    );
    this.patchCachedTemplate(workspaceSlug, detail.id, () => detail);
    this.staleCachedTemplates(workspaceSlug);
    track("template_updated", { scope: "project", template_id: id, fields, source });
    return detail;
  }

  async updateWorkitemCustomFields(
    workspaceSlug: string,
    templateId: string,
    workitemId: string,
    customFields: TTemplateCustomFieldEntry[],
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const response = await this.rootStore.projectTemplateService.updateWorkitemTemplateCustomFields(
      workspaceSlug,
      templateId,
      workitemId,
      customFields,
    );
    // Patch the cached detail in place so the editor sees fresh data without
    // a full re-fetch. Walks roots + sub_workitems since the BE accepts both.
    const patchDetail = (detail: TProjectTemplateDetail): TProjectTemplateDetail => {
      const patch = (item: TWorkitemTemplate): TWorkitemTemplate => {
        if (item.id === workitemId) {
          return {
            ...item,
            custom_fields: response.custom_fields,
            custom_fields_schema: response.custom_fields_schema,
          };
        }
        if (item.sub_workitems && item.sub_workitems.length > 0) {
          return { ...item, sub_workitems: item.sub_workitems.map(patch) };
        }
        return item;
      };
      return { ...detail, workitems: detail.workitems.map(patch) };
    };
    this.patchCachedTemplate(workspaceSlug, templateId, (cached) =>
      cached ? patchDetail(cached) : cached,
    );
    this.staleCachedTemplates(workspaceSlug);
    track("template_updated", {
      scope: "project",
      template_id: templateId,
      work_item_template_id: workitemId,
      fields: ["work_item_custom_fields"],
      source,
    });
  }

  /**
   * Re-read one template's detail from the server (NG-3509). The work-items
   * bulk import creates rows server-side, which the commit response doesn't
   * carry, so a cache patch can't show them — the detail is refetched instead,
   * and the editor re-hydrates from it. Resolves once the re-read completes.
   */
  async refetchTemplateDetail(workspaceSlug: string, templateId: string): Promise<void> {
    const scope = queryCacheScope(workspaceSlug, "ProjectTemplatesStore");
    const client = scope ? peekQueryClient() : null;
    if (!scope || !client) return;
    await client
      .invalidateQueries({ queryKey: templateDetailKey(scope, templateId) })
      .catch((error: unknown) => {
        console.warn("[ProjectTemplatesStore] refetching the template detail after import failed", {
          workspaceSlug,
          templateId,
          error,
        });
      });
  }

  async createFromTemplate(
    workspaceSlug: string,
    templateId: string,
    overrides: TCreateProjectFromTemplateOverrides,
    source: AnalyticsSource = "page",
  ): Promise<TCreatedProjectFromTemplate> {
    const created = await this.rootStore.projectTemplateService.createProjectFromTemplate(
      workspaceSlug,
      templateId,
      overrides,
    );
    // The new project shows at once on its page and in the project list.
    const cache = projectCacheFor(workspaceSlug);
    if (cache) appendProject(cache.client, cache.scope, created);
    track("project_created_from_template", {
      project_id: created.id,
      template_id: templateId,
      source,
    });
    return created;
  }

  async deleteTemplate(
    workspaceSlug: string,
    id: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.removeTemplate(workspaceSlug, id);
    track("template_deleted", { scope: "project", template_id: id, source });
  }

  /**
   * Delete a selection one by one, stopping at the first failure. One
   * `template_deleted` for the whole action, counting the templates that were
   * deleted, including when a later one fails.
   */
  async bulkDeleteTemplates(workspaceSlug: string, ids: readonly string[]): Promise<void> {
    let count = 0;
    try {
      for (const id of ids) {
        await this.removeTemplate(workspaceSlug, id);
        count += 1;
      }
    } finally {
      if (count > 0) track("template_deleted", { scope: "project", count, source: "bulk" });
    }
  }

  private async removeTemplate(workspaceSlug: string, id: string): Promise<void> {
    await this.rootStore.projectTemplateService.deleteTemplate(workspaceSlug, id);
    this.patchCachedTemplate(workspaceSlug, id, null);
    // Keep the settings table in sync without a refetch; a table that never
    // held the row keeps its count.
    await this.patchTablePages(
      workspaceSlug,
      (rows) => rows.filter((item) => item.id !== id),
      (data) => (data.pages.some((page) => page.results.some((item) => item.id === id)) ? -1 : 0),
    );
    this.staleCachedTemplates(workspaceSlug);
  }
}
